import { useMemo, useRef, useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import container from 'src/container';
import {
    CreateOrganizationPayload,
    Organization,
    OrganizationRolePermission,
    OrganizationStructureVersion,
    OrganizationType,
} from 'src/domain/organization';
import { Button } from 'src/components/Buttons/Button';
import Input, {
    Error as InputError,
} from 'src/components/FormComponents/Input';
import PasswordInput from 'src/components/FormComponents/PasswordInput';
import PhoneInput from 'src/components/FormComponents/PhoneInput';
import Form from 'src/components/FormComponents/Form';
import { checkIfEmailExist, checkIfPhoneNumberExist } from 'src/domain/user';
import { useNotification } from 'src/components/Notification/useNotfication';
import { mutate } from 'swr';
import { useHistory } from 'react-router-dom';
import { EnvelopeIcon, GroupIcon } from 'src/components/Icons/icons';
import { useTranslation } from 'react-i18next';
import { UpdateOrganizationDto } from 'src/api/mundu.params';
import { useConsumers } from 'src/view/hooks';
import { buildLeafLabelSuffixByGroupId } from 'src/utils/consumerLabelUtils';
import {
    CreateOrganizationSchema,
    EditOrganizationSchema,
    EditSeparatedOrganizationSchema,
    OrganizationFormValues,
} from './createOrganizationForm.schema';
import { OrganizationHierarchySection } from './OrganizationHierarchySection';
import { OrganizationGroupsSection } from './OrganizationGroupsSection';
import { OrganizationRolesSection } from './OrganizationRolesSection';
import { useNextOfKins } from 'src/view/hooks/nextOfKin';
import OrganizationAvatarInput, {
    HandleUploadOrganizationAvatar,
} from 'src/components/FormComponents/OrganizationAvatarInput';
import { OrganizationLicenceUsage } from './OrganizationLicenceUsage';

const {
    cradle: { munduClient },
} = container;

const sanitizeGroups = (
    groups: OrganizationFormValues['groups'],
): OrganizationFormValues['groups'] =>
    groups.map(group => ({
        ...group,
        parentId: group.parentId?.trim() ? group.parentId.trim() : undefined,
        levelId: group.levelId?.trim() ?? '',
    }));

const rolePermissionSet = new Set(Object.values(OrganizationRolePermission));
const requiredRolePermission = OrganizationRolePermission.readResidents;

const normalizeRolePermissions = (
    permissions?: OrganizationRolePermission[],
): OrganizationRolePermission[] => {
    const filtered = (permissions ?? []).filter(permission =>
        rolePermissionSet.has(permission),
    );
    const withoutRequired = filtered.filter(
        permission => permission !== requiredRolePermission,
    );
    return [requiredRolePermission, ...withoutRequired];
};

const sanitizeRoles = (
    roles: OrganizationFormValues['roles'],
): OrganizationFormValues['roles'] =>
    roles.map(role => ({
        ...role,
        groupIds: role.groupIds.filter(Boolean),
        permissions: normalizeRolePermissions(role.permissions),
    }));

const sanitizeHierarchy = (
    hierarchy: OrganizationFormValues['hierarchy'],
): OrganizationFormValues['hierarchy'] =>
    (() => {
        const usedIds = new Set<string>();
        const trimmed = hierarchy
            .map(level => ({
                id: level.id?.trim() || uuidv4(),
                name: level.name?.trim() ?? '',
                parentId: level.parentId?.trim()
                    ? level.parentId.trim()
                    : undefined,
            }))
            .filter(level => level.name.length > 0);

        const withStableIds = trimmed.map(level => {
            let nextId = level.id;

            while (usedIds.has(nextId)) {
                nextId = uuidv4();
            }

            usedIds.add(nextId);

            return {
                ...level,
                id: nextId,
            };
        });

        const idSet = new Set(withStableIds.map(level => level.id));

        return withStableIds.map(level => ({
            ...level,
            parentId:
                level.parentId &&
                level.parentId !== level.id &&
                idSet.has(level.parentId)
                    ? level.parentId
                    : undefined,
        }));
    })();

interface OrganizationFormProps {
    organization?: Organization;
    readOnly?: boolean;
    canEditTypeAndLicence?: boolean;
}

export const OrganizationForm = ({
    organization,
    readOnly = false,
    canEditTypeAndLicence = true,
}: OrganizationFormProps) => {
    const notify = useNotification();
    const history = useHistory();
    const { t } = useTranslation();
    const organizationId = organization?.id;
    const { consumers } = useConsumers({
        organizationId,
        enabled: Boolean(organizationId),
    });
    const { data: nextOfKins } = useNextOfKins({ organizationId });
    const avatarInputRef = useRef<HandleUploadOrganizationAvatar>(null);
    const formResetRef = useRef<UseFormReset<OrganizationFormValues> | null>(
        null,
    );
    const [isAvatarDirty, setIsAvatarDirty] = useState(false);

    const leafLabelSuffixByGroupId = useMemo(
        () =>
            buildLeafLabelSuffixByGroupId(consumers, nextOfKins, {
                organizationId,
            }),
        [consumers, nextOfKins, organizationId],
    );

    const primaryMembership = organization?.memberships?.[0];
    const isSeparatedOrganization =
        organization?.structureVersion ===
        OrganizationStructureVersion.separated;
    const hasActiveLegacyAdministrator = Boolean(
        organization &&
            !isSeparatedOrganization &&
            !organization.legacyAdministratorRetiredAt,
    );

    const defaultValues: Partial<OrganizationFormValues> = organization
        ? {
              ...(hasActiveLegacyAdministrator
                  ? {
                        firstName: organization.profile.firstName,
                        lastName: organization.profile.lastName,
                        email: organization.email,
                    }
                  : {}),
              licence: organization.licence,
              organizationName: organization.organizationName,
              organizationDisplayName: organization.organizationDisplayName,
              disableLogoDesignAdjustments:
                  organization.disableLogoDesignAdjustments ?? false,
              type: organization.type ?? OrganizationType.careHome,
              hierarchy: organization.hierarchy ?? [],
              groups: (
                  organization.groups ??
                  primaryMembership?.groups ??
                  []
              ).map(group => ({
                  ...group,
                  levelId: group.levelId ?? '',
              })),
              roles: (organization.roles ?? primaryMembership?.roles ?? []).map(
                  role => ({
                      ...role,
                      permissions: normalizeRolePermissions(role.permissions),
                  }),
              ),
          }
        : {
              hierarchy: [],
              groups: [],
              roles: [],
              type: OrganizationType.careHome,
              disableLogoDesignAdjustments: false,
          };

    const schema: new () => OrganizationFormValues = !organization
        ? CreateOrganizationSchema
        : hasActiveLegacyAdministrator
        ? EditOrganizationSchema
        : EditSeparatedOrganizationSchema;

    const isEdit = Boolean(organization);
    const showsLegacyAdministratorFields =
        !isEdit || hasActiveLegacyAdministrator;
    const submitLabel = isEdit
        ? t('admin.forms.createOrganization.update')
        : t('admin.forms.createOrganization.submit');

    const onSubmit = async (data: OrganizationFormValues): Promise<void> => {
        const licenceValue = Number(data.licence);

        const sanitizedGroups = sanitizeGroups(data.groups);
        const sanitizedRoles = sanitizeRoles(data.roles);
        const sanitizedHierarchy = sanitizeHierarchy(data.hierarchy);

        if (organization) {
            const payload: UpdateOrganizationDto = {
                ...(showsLegacyAdministratorFields
                    ? {
                          email: data.email,
                          firstName: data.firstName,
                          lastName: data.lastName,
                      }
                    : {}),
                hierarchy: sanitizedHierarchy,
                groups: sanitizedGroups,
                roles: sanitizedRoles,
                ...(canEditTypeAndLicence
                    ? { licence: licenceValue, type: data.type }
                    : {}),
                disableLogoDesignAdjustments:
                    data.disableLogoDesignAdjustments ?? false,
            };

            if (data.organizationName)
                payload.organizationName = data.organizationName;
            if (data.organizationDisplayName)
                payload.organizationDisplayName = data.organizationDisplayName;

            try {
                if (
                    showsLegacyAdministratorFields &&
                    organization.email !== data.email
                ) {
                    await checkIfEmailExist(data.email);
                }

                await munduClient.updateOganization(organization.id, payload);
                let isAvatarSaved = true;
                try {
                    await avatarInputRef.current?.uploadAvatar();
                } catch (avatarError) {
                    isAvatarSaved = false;
                    if (avatarError instanceof Error) {
                        notify(avatarError.message, true);
                    } else {
                        notify(t('admin.notifications.genericError'), true);
                    }
                }
                notify(t('admin.notifications.organizationUpdated'));
                formResetRef.current?.({
                    ...data,
                    licence: licenceValue,
                    hierarchy: sanitizedHierarchy,
                    groups: sanitizedGroups,
                    roles: sanitizedRoles,
                });
                setIsAvatarDirty(!isAvatarSaved);
                mutate(['orginfo', organization.id]);
                mutate(['organization', organization.id]);
                mutate('organizations');
                mutate('organizations-b2c');
                if (!isEdit) {
                    history.push(`/app/admin/organization/${organization.id}`);
                }
            } catch (e) {
                if (e instanceof Error) notify(e.message, true);
            }

            return;
        }

        const organizationName = data.organizationName;
        const organizationDisplayName = data.organizationDisplayName;
        const initialPassword = data.initialPassword;

        if (!organizationName || !organizationDisplayName || !initialPassword) {
            notify(t('admin.notifications.genericError'), true);
            return;
        }

        const payload: CreateOrganizationPayload = {
            organizationName,
            organizationDisplayName,
            profile: {
                firstName: data.firstName,
                lastName: data.lastName,
            },
            licence: licenceValue,
            disableLogoDesignAdjustments:
                data.disableLogoDesignAdjustments ?? false,
            email: data.email,
            ...(data.phoneNumber && { phoneNumber: data.phoneNumber }),
            initialPassword,
            type: data.type,
            hierarchy: sanitizedHierarchy.length
                ? sanitizedHierarchy
                : undefined,
            groups: sanitizedGroups.length ? sanitizedGroups : undefined,
            roles: sanitizedRoles.length ? sanitizedRoles : undefined,
        };

        try {
            await checkIfEmailExist(data.email);
            if (data.phoneNumber)
                await checkIfPhoneNumberExist(data.phoneNumber);
            const createdOrganization = await munduClient.createOrganization(
                payload,
            );
            try {
                await avatarInputRef.current?.uploadAvatar(createdOrganization);
            } catch (avatarError) {
                if (avatarError instanceof Error) {
                    notify(avatarError.message, true);
                } else {
                    notify(t('admin.notifications.genericError'), true);
                }
            }
            notify(t('admin.notifications.organizationCreated'));
            mutate('organizations-b2c');
            history.replace('/app/admin');
        } catch (e) {
            if (e instanceof Error) notify(e.message, true);
        }
    };

    return (
        <Form<OrganizationFormValues>
            schema={schema}
            className="w-full h-full"
            mode="onChange"
            onSubmit={onSubmit}
            defaultValues={defaultValues}
        >
            {({
                register,
                control,
                watch,
                reset,
                formState: { errors, isDirty, isSubmitting, isValid },
            }) => {
                const watchedGroups = watch('groups') ?? [];
                const watchedHierarchy = watch('hierarchy') ?? [];
                const hasUnsavedChanges =
                    !readOnly && isEdit && (isDirty || isAvatarDirty);
                formResetRef.current = reset;

                const organizationAvatar = (
                    <OrganizationAvatarInput
                        ref={avatarInputRef}
                        organization={organization}
                        readOnly={readOnly}
                        onAvatarDirtyChange={setIsAvatarDirty}
                        logoAdjustmentLabel={t(
                            'admin.forms.createOrganization.disableLogoDesignAdjustments',
                        )}
                        logoAdjustmentHelperText={t(
                            'admin.forms.createOrganization.disableLogoDesignAdjustmentsHelper',
                        )}
                        logoAdjustmentInputProps={register(
                            'disableLogoDesignAdjustments',
                        )}
                    />
                );

                const administratorFields = showsLegacyAdministratorFields ? (
                    <>
                        <Input
                            className="text-input"
                            label={t(
                                'admin.forms.createOrganization.firstName',
                            )}
                            error={errors.firstName}
                            readOnly={readOnly}
                            {...register('firstName')}
                        />
                        <Input
                            className="text-input"
                            label={t('admin.forms.createOrganization.lastName')}
                            error={errors.lastName}
                            readOnly={readOnly}
                            {...register('lastName')}
                        />

                        {isEdit ? (
                            <Input
                                className="text-input"
                                label={t(
                                    'admin.forms.createOrganization.phone',
                                )}
                                value={organization?.phoneNumber ?? '—'}
                                readOnly
                                disabled
                            />
                        ) : (
                            <PhoneInput
                                className="text-input"
                                type="tel"
                                error={errors.phoneNumber}
                                readOnly={readOnly}
                                label={t(
                                    'admin.forms.createOrganization.phone',
                                )}
                                {...register('phoneNumber')}
                            />
                        )}

                        {isEdit ? (
                            <>
                                <Input
                                    className="text-input"
                                    type="email"
                                    leadingIcon={EnvelopeIcon}
                                    label={t(
                                        'admin.forms.createOrganization.email',
                                    )}
                                    error={errors.email}
                                    value={organization?.email ?? ''}
                                    readOnly
                                    disabled
                                />
                                <input
                                    type="hidden"
                                    {...register('email')}
                                    defaultValue={organization?.email ?? ''}
                                />
                            </>
                        ) : (
                            <Input
                                className="text-input"
                                type="email"
                                leadingIcon={EnvelopeIcon}
                                label={t(
                                    'admin.forms.createOrganization.email',
                                )}
                                error={errors.email}
                                readOnly={readOnly}
                                {...register('email')}
                            />
                        )}

                        {!isEdit ? (
                            <PasswordInput
                                className="text-input"
                                autoComplete="new-password"
                                label={t(
                                    'admin.forms.createOrganization.initialPassword',
                                )}
                                error={errors.initialPassword}
                                readOnly={readOnly}
                                {...register('initialPassword')}
                            />
                        ) : null}
                    </>
                ) : null;

                const organizationFields = (
                    <>
                        <Input
                            className="text-input"
                            leadingIcon={GroupIcon}
                            type="text"
                            label={t(
                                'admin.forms.createOrganization.shortName',
                            )}
                            error={errors.organizationName}
                            readOnly={readOnly}
                            {...register('organizationName')}
                        />
                        <Input
                            className="text-input"
                            type="text"
                            leadingIcon={GroupIcon}
                            label={t(
                                'admin.forms.createOrganization.displayName',
                            )}
                            error={errors.organizationDisplayName}
                            readOnly={readOnly}
                            {...register('organizationDisplayName')}
                        />

                        {canEditTypeAndLicence ? (
                            <>
                                <div className="flex flex-col gap-1">
                                    <label className="text-sm font-medium text-woodyBrown">
                                        {t(
                                            'admin.forms.createOrganization.type',
                                        )}
                                    </label>
                                    <select
                                        className="text-input"
                                        disabled={readOnly}
                                        {...register('type')}
                                        defaultValue={defaultValues.type}
                                    >
                                        <option
                                            value={OrganizationType.careHome}
                                        >
                                            {t(
                                                'admin.forms.createOrganization.typeCareHome',
                                            )}
                                        </option>
                                        <option
                                            value={OrganizationType.nursingHome}
                                        >
                                            {t(
                                                'admin.forms.createOrganization.typeNursingHome',
                                            )}
                                        </option>
                                    </select>
                                    <InputError error={errors.type} />
                                </div>

                                <Input
                                    className="text-input"
                                    type="number"
                                    label={t(
                                        'admin.forms.createOrganization.licences',
                                    )}
                                    error={errors.licence}
                                    readOnly={readOnly}
                                    {...register('licence')}
                                />
                            </>
                        ) : (
                            <OrganizationLicenceUsage />
                        )}
                    </>
                );

                return (
                    <div
                        className={
                            hasUnsavedChanges
                                ? 'flex flex-col pb-24'
                                : 'flex flex-col'
                        }
                    >
                        <div>
                            <div id="org-settings">
                                {!isEdit ? (
                                    <div className="space-y-6">
                                        <section className="rounded-2xl border border-starkWhite bg-white p-4 shadow-sm sm:p-5">
                                            <h2 className="text-lg font-semibold text-woodyBrown">
                                                {t(
                                                    'admin.forms.createOrganization.organizationDetailsTitle',
                                                )}
                                            </h2>
                                            <div className="mt-5">
                                                {organizationAvatar}
                                            </div>
                                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                                {organizationFields}
                                            </div>
                                        </section>

                                        <section className="rounded-2xl border border-starkWhite bg-white p-4 shadow-sm sm:p-5">
                                            <h2 className="text-lg font-semibold text-woodyBrown">
                                                {t(
                                                    'admin.forms.createOrganization.administratorDetailsTitle',
                                                )}
                                            </h2>
                                            <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
                                                {administratorFields}
                                            </div>
                                        </section>
                                    </div>
                                ) : (
                                    <>
                                        {organizationAvatar}
                                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                            {administratorFields}
                                            {organizationFields}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="pt-6 flex flex-col gap-6">
                                <hr className="border-woodyBrown border-1 opacity-30" />
                                <div id="org-hierarchy">
                                    <OrganizationHierarchySection
                                        control={control}
                                        register={register}
                                        errors={errors}
                                        t={t}
                                        hierarchy={watchedHierarchy}
                                        readOnly={readOnly}
                                    />
                                </div>

                                <hr className="border-woodyBrown border-1 opacity-30" />
                                <div id="org-groups">
                                    <OrganizationGroupsSection
                                        control={control}
                                        register={register}
                                        errors={errors}
                                        t={t}
                                        groups={watchedGroups}
                                        readOnly={readOnly}
                                    />
                                </div>

                                <hr className="border-woodyBrown border-1 opacity-30" />
                                <div id="org-roles">
                                    <OrganizationRolesSection
                                        control={control}
                                        register={register}
                                        errors={errors}
                                        t={t}
                                        groups={watchedGroups}
                                        readOnly={readOnly}
                                        leafLabelSuffixByGroupId={
                                            leafLabelSuffixByGroupId
                                        }
                                    />
                                </div>
                            </div>

                            {!readOnly && (
                                <div className="inline-flex justify-end py-6 items-end w-full">
                                    <Button submit title={submitLabel} />
                                </div>
                            )}

                            {hasUnsavedChanges ? (
                                <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-woodyBrown/20 bg-lightPeach px-4 py-3 shadow-super md:left-64">
                                    <div className="mx-auto flex w-full justify-end px-3">
                                        <Button
                                            submit
                                            active={!isSubmitting && isValid}
                                            disabled={isSubmitting || !isValid}
                                            title={submitLabel}
                                        />
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                );
            }}
        </Form>
    );
};
